import { atom, getDefaultStore } from "jotai";

import type { CustomerDetails } from "../schemas/customer-schema";

const SELECTED_CUSTOMER_STORAGE_KEY = "selectedCustomers";

/**
 * Hydrates the selected customer atom synchronously at module load.
 *
 * The app also writes this value via `useEffect` in `CustomerDataInitializer`,
 * but child effects run before parent effects — so on a refresh the first
 * external API call could fire before the atom is populated. Reading
 * sessionStorage up front makes the cached customer available on the very
 * first render, which lets `setExternalCustomerHeaders` attach the right
 * `Tql-Identity-Companyid` from request #1.
 */
const getInitialSelectedCustomer = (): CustomerDetails | null => {
	try {
		const raw =
			globalThis.sessionStorage?.getItem(SELECTED_CUSTOMER_STORAGE_KEY) ?? null;
		return raw ? (JSON.parse(raw) as CustomerDetails) : null;
	} catch {
		return null;
	}
};

// Atoms for customer selection
export const selectedCustomerAtom = atom<CustomerDetails | null>(
	getInitialSelectedCustomer(),
);
export const availableCustomersAtom = atom<CustomerDetails[]>([]);
export const userCustomersQueryFailedAtom = atom<boolean>(false);

/**
 * Clears the cached selected customer from both sessionStorage and the
 * in-memory atom.
 *
 * The atom is hydrated from sessionStorage at module load, so on a same-tab
 * re-login / re-auth the previously selected customer would otherwise survive
 * and prevent `CustomerDataInitializer` from re-running its login-default
 * logic (it bails when `selectedCustomer` is already set). Calling this on
 * login (see `b2c-utils.ts`) forces `resolveInitialCustomer` to re-evaluate so
 * an external `@tql.com` user is always returned to the default customer.
 */
export const clearSelectedCustomer = (): void => {
	try {
		globalThis.sessionStorage?.removeItem(SELECTED_CUSTOMER_STORAGE_KEY);
	} catch {
		// sessionStorage may be unavailable (e.g. SSR / privacy mode); the
		// in-memory reset below is still applied.
	}
	getDefaultStore().set(selectedCustomerAtom, null);
};
